import type { SubmitHandler, UseFormReturn } from '@portals-platform/ui-forms';
import { useNavigate } from '@tanstack/react-router';
import type { CertificateResponse } from '../../../data-access/api/responses.js';
import {
  useCaptivePolicies,
  useCreateCertificate,
  useEditCertificate,
} from '../../../data-access/index.js';
import { useServerError } from '../../../hooks/use-server-error.js';
import { getPolicyForYear } from '../policy.js';
import type { FormValues } from '../types.js';

export function useSubmitCertificate(
  form: UseFormReturn<FormValues>,
  initialCertificate?: CertificateResponse,
) {
  const navigate = useNavigate();
  const createCertificate = useCreateCertificate();
  const editCertificate = useEditCertificate();
  const policies = useCaptivePolicies();
  const { serverError, handleServerError } = useServerError(form);

  const captivePolicyIdFor = (values: FormValues) =>
    getPolicyForYear({ policies, policyYear: values.policyYear })?.id ?? '';

  const submit =
    (isCertificateLive: boolean): SubmitHandler<FormValues> =>
    async (values) => {
      try {
        await createCertificate.mutateAsync({
          input: values,
          captivePolicyId: captivePolicyIdFor(values),
          isCertificateLive,
        });
        void navigate({ to: '/certificates' });
      } catch (error) {
        handleServerError(error);
      }
    };

  const saveChanges: SubmitHandler<FormValues> = async (values) => {
    if (!initialCertificate) {
      return;
    }

    try {
      await editCertificate.mutateAsync({
        certificateId: initialCertificate.id,
        input: values,
        captivePolicyId: captivePolicyIdFor(values),
      });
      void navigate({
        to: '/certificates/$certificateId',
        params: { certificateId: initialCertificate.id },
      });
    } catch (error) {
      handleServerError(error);
    }
  };

  return { submit, saveChanges, serverError };
}
